import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import React, { Component } from 'react';
import dompurify from 'dompurify';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { constructTaskObject } from '@nile/projects/src/Utils';

import { Button, ButtonGroup, Page, Heading } from '@superops/ui-design-system';
import {
  toast,
  validateForm,
  preFormatForm,
  ProjectTemplateConstraints,
  EventEmitter,
  CardPage,
  getParsedCrudData,
  useActivityTracker,
  PSA_CONSTANTS
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { cloneDeep, omit, sortBy } from 'lodash';
import {
  CreateMilestoneTaskCard,
  MilestoneCard,
  OtherTasksCard,
  ProjectDescription,
  ProjectDetail
} from '@nile/projects/src/Components/Cards';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import nanoid from 'nanoid';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';

const hideSectionsInTaskTemplateForm = ['status'];

class ProjectTemplateForm extends Component {
  static contextType = TabContext;

  constructor(props) {
    super(props);
    this.state = props?.activeTab?.state?.project
      ? props.activeTab.state.project
      : props.data
      ? {
          templateId: props.data.templateId,
          submitActionLoading: false,
          title: props.data.name,
          template: {
            id: props.data.templateId,
            name: props.data.name,
            content: props.data.description,
            placeholdersUsed: '',
            mentions: [],
            attachments: props.data.attachments || [],
            inlineImages: props.data.inlineImages || []
          },
          scheduledStartDate: props.data.scheduledStartDate || {
            unit: 'CUSTOM',
            value: 'TODAY'
          },
          dueDate: props.data.dueDate || null,
          milestones: props.data.milestones
            ? props.data.milestones.map((milestone) => ({
                milestoneId: milestone.milestoneId,
                name: milestone.name,
                addTasks: milestone.tasks || [],
                milestoneOrder: milestone.milestoneOrder
              }))
            : [],
          tasks: props.data.tasks || [],
          errors: {}
        }
      : {
          templateId: null,
          submitActionLoading: false,
          title: '',
          template: {
            name: '',
            content: '',
            placeholdersUsed: '',
            mentions: [],
            attachments: [],
            inlineImages: []
          },
          scheduledStartDate: {
            unit: 'CUSTOM',
            value: 'TODAY'
          },
          dueDate: null,
          milestones: [],
          tasks: [],
          errors: {}
        };
  }

  componentDidMount() {
    EventEmitter.subscribe('tabChange', this.updateTabState);
  }

  componentWillUnmount() {
    EventEmitter.unsubscribe('tabChange');
  }

  updateTabState = (eventData) => {
    this.context.updateTabState(
      {
        project: this.state
      },
      eventData
    );
  };

  resetTabState = () => {
    if (this.context.activeTab?.state?.project) this.context.resetTabState();
  };

  updateTemplate = (obj) => {
    const updatedTemplate = cloneDeep(this.state.template);
    this.setState({ template: { ...updatedTemplate, ...obj } });
  };

  handleChange = (val, target) => {
    this.setState({ [target]: val });
  };

  handleMilestoneCreation = () => {
    const { milestones: addMilestones } = this.state;
    const newMilestone = {
      name: '',
      addTasks: [],
      milestoneId: nanoid(),
      milestoneOrder: addMilestones.length + 1
    };

    this.setState({ milestones: [...addMilestones, newMilestone] });
  };

  handleOtherTasksAddition = (task) => {
    const { tasks: addTasks } = this.state;
    const updatedTasks = [...addTasks];
    updatedTasks.push({ ...task, taskOrder: (addTasks?.length ?? 0) + 1 });
    this.setState({ tasks: updatedTasks });
  };

  handleOtherTaskTemplateAddition = (tasks) => {
    const { tasks: addTasks } = this.state;
    const updatedTasks = [
      ...addTasks,
      ...tasks.map((t, index) => ({
        ...t,
        taskOrder: (addTasks?.length ?? 0) + index + 1
      }))
    ];
    this.setState({ tasks: updatedTasks });
  };

  handleMilestoneUpdate = (_, obj) => {
    const { milestones: addMilestones } = this.state;

    const updatedMilestones = [...addMilestones].map((m) => {
      if (m.milestoneId === obj.milestoneId) {
        return {
          ...obj,
          addTasks: obj.addTasks.map((item, i) => ({
            ...item,
            taskOrder: i + 1
          }))
        };
      }

      return m;
    });

    this.setState({
      milestones: updatedMilestones
    });
  };

  handleMilestoneDeletion = (milestoneId) => {
    const { milestones: addMilestones } = this.state;

    const updatedMilestones = addMilestones
      .filter((m) => m.milestoneId !== milestoneId)
      .map((m, i) => ({ ...m, milestoneOrder: i + 1 }));

    this.setState({ milestones: updatedMilestones });
  };

  resetErrors = (type) => {
    const { errors } = this.state;
    const updatedErrors = { ...errors };
    delete updatedErrors[type];
    this.setState({ errors: updatedErrors });
  };

  formatTaskStructure = (task, keepId) => {
    const { taskTemplateId, techGroup, technician, ...rest } = task;
    const formattedTask = { ...rest };
    if (keepId && taskTemplateId) formattedTask.taskTemplateId = taskTemplateId;
    if (techGroup)
      formattedTask.techGroup = {
        groupId: techGroup.groupId
      };
    if (technician) formattedTask.technician = { userId: technician.userId };
    return formattedTask;
  };

  createProject = () => {
    if (this.props?.actions?.primaryAction) {
      const {
        title,
        template,
        scheduledStartDate,
        dueDate,
        templateId,
        tasks,
        milestones
      } = this.state;
      const reqPayload = {
        name: title,
        description: template?.content,
        scheduledStartDate: scheduledStartDate?.value?.toString()
          ? scheduledStartDate
          : {
              unit: 'CUSTOM',
              value: 'TODAY'
            },
        dueDate: dueDate?.value?.toString() ? dueDate : null
      };

      // Validate form
      const dummyForm = {
        title,
        description: template?.content ? dompurifier(template.content) : '',
        scheduledStartDate,
        dueDate,
        addMilestones: milestones,
        tasks
      };
      const validationResult = validateForm(
        preFormatForm(dummyForm),
        ProjectTemplateConstraints
      );
      if (validationResult) {
        this.setState({ errors: validationResult, submitActionLoading: false });
        return;
      }

      // Create template
      if (!templateId) {
        // Add milestones
        reqPayload.addMilestones = milestones.map((milestone) => ({
          name: milestone.name,
          addTasks: milestone.addTasks.map((el) =>
            this.formatTaskStructure(el)
          ),
          milestoneOrder: milestone.milestoneOrder
        }));
        // Add tasks
        reqPayload.addTasks = tasks.map((el) => this.formatTaskStructure(el));
        // Add attachments
        if (template.attachments?.length) {
          reqPayload.addAttachments = template.attachments?.map(
            ({ fileName, fileSize, originalFileName }) => ({
              fileName,
              fileSize,
              originalFileName
            })
          );
        }
      }

      // Update template
      if (templateId) {
        // Add template id
        reqPayload.templateId = templateId;
        // Add milestones
        const baseMilestones =
          this.props.data?.milestones?.map((milestone) => ({
            milestoneId: milestone.milestoneId,
            name: milestone.name,
            addTasks: milestone.tasks || [],
            milestoneOrder: milestone.milestoneOrder
          })) || [];
        const {
          addedItems: addMilestones,
          deletedItems: deleteMilestones,
          updatedItems: updateMilestones
        } = getParsedCrudData(baseMilestones, milestones, 'milestoneId');
        reqPayload.addMilestones = addMilestones.map((milestone) => ({
          name: milestone.name,
          addTasks: milestone.addTasks.map((el) =>
            this.formatTaskStructure(el)
          ),
          milestoneOrder: milestone.milestoneOrder
        }));
        reqPayload.updateMilestones = updateMilestones.map((milestone) => {
          const baseTasks =
            this.props.data?.milestones?.find(
              (el) => el.milestoneId === milestone.milestoneId
            )?.tasks || [];
          const {
            addedItems: addTasks,
            deletedItems: deleteTasks,
            updatedItems: updateTasks
          } = getParsedCrudData(
            baseTasks,
            milestone.addTasks,
            'taskTemplateId'
          );
          return {
            milestoneId: milestone.milestoneId,
            name: milestone.name,
            addTasks: addTasks.map((el) => this.formatTaskStructure(el)),
            updateTasks: updateTasks.map((el) =>
              this.formatTaskStructure(el, true)
            ),
            deleteTasks: deleteTasks.map(({ taskTemplateId }) => ({
              taskTemplateId
            })),
            milestoneOrder: milestone.milestoneOrder
          };
        });
        reqPayload.deleteMilestones = deleteMilestones.map(
          ({ milestoneId }) => ({
            milestoneId
          })
        );
        // Add tasks
        const {
          addedItems: addTasks,
          deletedItems: deleteTasks,
          updatedItems: updateTasks
        } = getParsedCrudData(
          this.props.data?.tasks || [],
          tasks,
          'taskTemplateId'
        );
        reqPayload.addTasks = addTasks.map((el) =>
          this.formatTaskStructure(el)
        );
        reqPayload.updateTasks = updateTasks.map((el) =>
          this.formatTaskStructure(el, true)
        );
        reqPayload.deleteTasks = deleteTasks.map(({ taskTemplateId }) => ({
          taskTemplateId
        }));
        // Add attachments
        const {
          addedItems: addAttachments,
          deletedItems: deleteAttachments
        } = getParsedCrudData(
          this.props.data?.attachments || [],
          template.attachments?.map(
            ({ fileName, fileSize, originalFileName }) => ({
              fileName,
              fileSize,
              originalFileName
            })
          ),
          'fileName'
        );
        reqPayload.addAttachments = addAttachments;
        reqPayload.deleteAttachments = deleteAttachments;
      }

      // Add inline images
      if (this.state.template.inlineImages?.length) {
        reqPayload.inlineImages = this.state.template.inlineImages;
      }

      // Update/create api call
      this.setState({ errors: {}, submitActionLoading: true });
      this.props.actions
        .primaryAction({
          variables: { input: reqPayload },
          input: reqPayload
        })
        .then((response) => {
          this.resetTabState();
          if (response?.data?.updateProjectTemplate) {
            toast('success', 'Project template updated successfully');
            if (this.props?.actions?.back) this.props.actions.back();
          }
          if (response?.data?.createProjectTemplate) {
            useActivityTracker({
              event: `Template - Project created`
            });
            toast('success', 'Project template created successfully');
            if (this.props?.actions?.back) this.props.actions.back();
          }
        })
        .finally(() => this.setState({ submitActionLoading: false }));
    }
  };

  getAllTasksNameList = () => {
    let tasksList = [];
    tasksList = this.state.tasks ? [...this.state.tasks] : [];
    if (this.state.milestones)
      this.state.milestones.forEach(({ addTasks }) => {
        tasksList = [...tasksList, ...addTasks];
      });
    return tasksList.map(({ title }) => title);
  };

  render() {
    const {
      templateId,
      title,
      template,
      scheduledStartDate,
      dueDate,
      submitActionLoading,
      milestones: addMilestones,
      tasks: addTasks,
      errors
    } = this.state;

    const sortedAddMilestones = sortBy(addMilestones, 'milestoneOrder');

    return (
      <CardPage
        height="74px"
        className="p12 white-grey-page"
        headerPadding="20px 40px"
        contentPadding="20px 40px"
        noMargin
        contentHeight="81px"
      >
        <Wrapper>
          <Page
            variant="primary"
            contentHeight="100px"
            title={
              <div className="flex actr g20">
                <div className="flex actr">
                  <Heading element="h1">
                    <FormattedMessage
                      id={
                        templateId
                          ? 'admin.projectTemplates.title.edit.header'
                          : 'admin.projectTemplates.title.create.header'
                      }
                    />
                  </Heading>
                </div>
                <CreateMilestoneTaskCard
                  addMilestone={this.handleMilestoneCreation}
                  handleOtherTasksAddition={this.handleOtherTasksAddition}
                  handleOtherTaskTemplateAddition={
                    this.handleOtherTaskTemplateAddition
                  }
                  taskTemplates={this.props?.taskTemplates}
                  getSelectedTaskNameList={this.getAllTasksNameList}
                  module="PROJECT_TEMPLATE_TASK_TEMPLATE"
                  hideSectionsInTaskForm={hideSectionsInTaskTemplateForm}
                  sendAllKeysInTemplate
                  isTemplate
                />
              </div>
            }
            primaryAction={
              <div className="fx fx-sb actr">
                <ButtonGroup>
                  <Button
                    plainSecondary
                    onClick={() => {
                      this.resetTabState();
                      if (this.props?.actions?.back) this.props.actions.back();
                    }}
                  >
                    <FormattedMessage id="common.cancel" />
                  </Button>
                  <Button
                    primary
                    onClick={this.createProject}
                    loading={submitActionLoading}
                  >
                    <FormattedMessage id="common.save" />
                  </Button>
                </ButtonGroup>
              </div>
            }
          >
            <FormContentContainer>
              <ProjectDescription
                title={title}
                template={template}
                updateTemplate={this.updateTemplate}
                handleChange={this.handleChange}
                resetErrors={this.resetErrors}
                errors={errors}
                meta={{ titlePlaceholder: 'admin.projectTemplate.name.pl' }}
                module="PROJECT_TEMPLATE"
                attachmentModule="PROJECT_TEMPLATE_ATTACHMENT"
                hideStatus
              />
              <ProjectDetail
                scheduledStartDate={scheduledStartDate}
                dueDate={dueDate}
                errors={errors}
                resetErrors={this.resetErrors}
                handleChange={this.handleChange}
                isTemplate
              />
              <SuperSortable
                onDragEnd={(reorderedMilestone) => {
                  const newList = reorderedMilestone.map((m, i) => ({
                    ...m,
                    milestoneOrder: i + 1
                  }));
                  useActivityTracker({
                    event:
                      PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER[
                        this.state.templateId
                          ? 'PROJECT_TEMPLATE_EDIT'
                          : 'PROJECT_TEMPLATE_CREATE'
                      ].MILESTONE
                  });
                  this.setState({ milestones: newList });
                }}
                list={sortedAddMilestones}
              >
                {sortedAddMilestones.map((milestone, index) => (
                  <MilestoneCard
                    key={`milestone-${milestone?.milestoneId}`}
                    index={index}
                    milestoneId={milestone?.milestoneId}
                    title={milestone?.name}
                    tasks={sortBy(milestone?.addTasks, 'taskOrder')}
                    updateMilestone={(_, m) => {
                      this.handleMilestoneUpdate(_, { ...milestone, ...m });
                    }}
                    taskTemplates={this.props?.taskTemplates}
                    getSelectedTaskNameList={this.getAllTasksNameList}
                    deleteMilestone={() => {
                      this.handleMilestoneDeletion(milestone.milestoneId);
                    }}
                    module="PROJECT_TEMPLATE_TASK_TEMPLATE"
                    hideSectionsInTaskForm={hideSectionsInTaskTemplateForm}
                    errors={
                      errors?.addMilestones?.[0]?.errors?.[index]?.error?.name
                    }
                    sendAllKeysInTemplate
                    isTemplate
                    onTasksReorder={(reorderedTasks) => {
                      useActivityTracker({
                        event:
                          PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER[
                            this.state.templateId
                              ? 'PROJECT_TEMPLATE_EDIT'
                              : 'PROJECT_TEMPLATE_CREATE'
                          ].TASK
                      });
                      this.setState({
                        milestones: sortedAddMilestones.map((m) => {
                          const clonedMilestone = { ...m };

                          if (
                            m.milestoneId &&
                            m.milestoneId === milestone.milestoneId
                          ) {
                            clonedMilestone.addTasks = reorderedTasks;
                            return clonedMilestone;
                          }

                          return clonedMilestone;
                        })
                      });
                    }}
                  />
                ))}
              </SuperSortable>
              {addTasks?.length > 0 && (
                <OtherTasksCard
                  tasks={sortBy(addTasks, 'taskOrder')}
                  handleOtherTasksAddition={this.handleOtherTasksAddition}
                  handleOtherTaskTemplateAddition={
                    this.handleOtherTaskTemplateAddition
                  }
                  taskTemplates={this.props?.taskTemplates}
                  getSelectedTaskNameList={this.getAllTasksNameList}
                  updateOtherTasks={(otherTasks) => {
                    this.setState({ tasks: otherTasks });
                  }}
                  module="PROJECT_TEMPLATE_TASK_TEMPLATE"
                  hideSectionsInTaskForm={hideSectionsInTaskTemplateForm}
                  sendAllKeysInTemplate
                  isTemplate
                  onTasksReorder={(newOrderedList) => {
                    useActivityTracker({
                      event:
                        PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER[
                          this.state.templateId
                            ? 'PROJECT_TEMPLATE_EDIT'
                            : 'PROJECT_TEMPLATE_CREATE'
                        ].TASK
                    });
                    this.setState({ tasks: newOrderedList });
                  }}
                />
              )}
            </FormContentContainer>
          </Page>
        </Wrapper>
      </CardPage>
    );
  }
}

const Wrapper = styled.div`
  .Polaris-Page-Header__TitleActionMenuWrapper {
    width: fit-content;
    flex: none;
  }
  .Polaris-Page-Header__PrimaryActionWrapper {
    flex: 1;
  }
  .Polaris-Page {
    background: var(--sops-white-to-grey-bg) !important;
  }
`;

const FormContentContainer = styled.div`
  /* height: calc(100vh - 130px);
  overflow: auto; */
  padding: 0px;
  padding-top: 0 !important;
  /* > div {
    margin-bottom: 30px;
  } */
  .Polaris-Subheading {
    padding-top: 6px;
  }
  .year-input {
    width: 200px;
  }
`;

export default withRouter(injectIntl(ProjectTemplateForm));
